import { useState } from "react";
import { Loader2, ShieldCheck } from "lucide-react";
import { Button } from "@/components/ui/button";
import DonationSettingsFields from "@/components/DonationSettingsFields";
import PublicVisibilitySettings from "@/components/PublicVisibilitySettings";
import SanctuarySettingsFields from "@/components/SanctuarySettingsFields";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";

export default function AdminCaseSettings({ caseItem, onUpdated }) {
  const [donationReceived, setDonationReceived] = useState(caseItem.donation_received ?? 0);
  const [razorpayLink, setRazorpayLink] = useState(caseItem.razorpay_link || "");
  const [isPublic, setIsPublic] = useState(caseItem.is_public);
  const [publicStory, setPublicStory] = useState(caseItem.public_story || "");
  const [monthlyCareCost, setMonthlyCareCost] = useState(caseItem.monthly_care_cost ?? 0);
  const [sponsorLink, setSponsorLink] = useState(caseItem.sponsor_razorpay_link || "");
  const [sanctuaryStory, setSanctuaryStory] = useState(caseItem.sanctuary_story || "");
  const [arrivalDate, setArrivalDate] = useState(caseItem.arrival_at_sanctuary_date || "");
  const [isSponsored, setIsSponsored] = useState(caseItem.is_sponsored || false);
  const [featured, setFeatured] = useState(caseItem.featured || false);
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const isSanctuary = caseItem.status === "Sanctuary Resident";
  const canBePublic = caseItem.status !== "Passed Away";
  const missingDonationSetup = !caseItem.donation_needed || !razorpayLink;

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/cases/${caseItem.id}`, {
        donation_received: parseFloat(donationReceived) || 0,
        razorpay_link: razorpayLink || null,
        is_public: canBePublic ? isPublic : false,
        public_story: publicStory,
        featured,
        ...(isSanctuary && {
          monthly_care_cost: parseFloat(monthlyCareCost) || 0,
          sponsor_razorpay_link: sponsorLink,
          sanctuary_story: sanctuaryStory,
          arrival_at_sanctuary_date: arrivalDate,
          is_sponsored: isSponsored,
        }),
      });
      toast({ title: "Admin settings saved" });
      onUpdated();
    } catch (err) {
      toast({ variant: "destructive", title: "Could not save", description: formatApiErrorDetail(err.response?.data?.detail) });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-amtm-surface border border-amtm-border rounded-2xl p-5 space-y-4" data-testid="admin-case-settings">
      <div className="flex items-center gap-2 text-amtm-teal font-semibold">
        <ShieldCheck className="h-5 w-5" /> Admin Settings
      </div>

      <DonationSettingsFields
        donationReceived={donationReceived}
        setDonationReceived={setDonationReceived}
        razorpayLink={razorpayLink}
        setRazorpayLink={setRazorpayLink}
        autoDonationTotal={caseItem.auto_donation_total || 0}
        donorCount={caseItem.donor_count || 0}
        featured={featured}
        setFeatured={setFeatured}
      />

      <PublicVisibilitySettings
        publicStory={publicStory}
        setPublicStory={setPublicStory}
        isPublic={isPublic}
        setIsPublic={setIsPublic}
        canBePublic={canBePublic}
        showDonationTip={isPublic && canBePublic && missingDonationSetup}
      />

      {isSanctuary && (
        <SanctuarySettingsFields
          monthlyCareCost={monthlyCareCost}
          setMonthlyCareCost={setMonthlyCareCost}
          sponsorLink={sponsorLink}
          setSponsorLink={setSponsorLink}
          sanctuaryStory={sanctuaryStory}
          setSanctuaryStory={setSanctuaryStory}
          arrivalDate={arrivalDate}
          setArrivalDate={setArrivalDate}
          isSponsored={isSponsored}
          setIsSponsored={setIsSponsored}
        />
      )}

      <Button onClick={handleSave} disabled={saving} className="w-full h-11 bg-amtm-text text-amtm-bg hover:bg-amtm-text/90" data-testid="save-admin-settings-button">
        {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Save Admin Settings"}
      </Button>
    </div>
  );
}
